import type {
  ArtifactCreationBody,
  ArtifactSimpleResource,
  CreateSmartFieldSchema,
  FormBuilderComponents,
  InputCustomModel,
  InputModel,
  SmartFieldCategoryOption,
  Translations,
} from '@ingenico/react-form-builder';
import { useCurrentUserDateTimeFormat } from '@ingenico/react-section-utils';
import { Banner } from '@ingenico/react-ui/banner';

import { componentsAtom, CreateSmartField, HydrateAtoms, translationsAtom } from '@ingenico/react-form-builder';
import type { AutocompleteDeleteEvent } from '@ingenico/react-ui/form-section';
import { Provider } from 'jotai';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { getSmartFieldCreationInput } from '../parameter-module-map-smart-fields-modal/ParameterModuleMapSmartFieldsModal.util';
import { ParameterModuleSmartFieldDataFileInput } from './ParameterModuleSmartFieldDataFileInput.ctrl';
import styles from './ParameterModuleSmartFieldContent.module.scss';

export function ParameterModuleSmartFieldCreateContent({
  translations,
  parameter,
  handleFormValidation,
  onFormChange,
  categories,
  isCategoryFeatureEnabled,
  artifacts,
  artifactsIsLoading,
  onArtifactSearch,
  onArtifactCreate,
  onArtifactPreview,
}: {
  readonly translations: Translations;
  readonly parameter: InputModel<InputCustomModel>;
  readonly handleFormValidation: (params: {
    readonly newSmartFieldWithNameAndDescriptionFormIsValid: boolean;
    readonly smartFieldFormIsValid: boolean;
  }) => void;
  readonly onFormChange: (form: CreateSmartFieldSchema) => void;
  readonly isCategoryFeatureEnabled: boolean;
  readonly artifacts: ArtifactSimpleResource[];
  readonly artifactsIsLoading: boolean;
  readonly onArtifactSearch: (query: string, acceptedExtensions?: readonly string[]) => void;
  readonly onArtifactCreate: (
    payload: ArtifactCreationBody,
    onSuccess?: (artifact: ArtifactSimpleResource) => void,
    onError?: () => void,
  ) => void;
  readonly onArtifactPreview: (id: string) => void;
  readonly categories: {
    readonly categoryOptions: SmartFieldCategoryOption[];
    readonly isLoading: boolean;
    readonly handleCategoryChange: (
      newValue: (SmartFieldCategoryOption | string)[] | null,
      onChange: (value: SmartFieldCategoryOption[]) => void,
      currentFormValue: SmartFieldCategoryOption[],
    ) => Promise<void>;
    readonly handleCategoryDelete: (
      event: AutocompleteDeleteEvent,
      categories: SmartFieldCategoryOption[],
      setCategories: (value: SmartFieldCategoryOption[]) => void,
    ) => void;
    readonly handleSearchChange?: (value: string) => void;
  };
}): ReactNode {
  const fgComponents: FormBuilderComponents = {
    dateTimeFormat: useCurrentUserDateTimeFormat(),
    banner: Banner,
  };
  const smartFieldCreationInput = useMemo(() => getSmartFieldCreationInput(parameter), [parameter]);

  return (
    <div className={styles.contentContainer}>
      <Provider>
        <HydrateAtoms
          initialValues={[
            [translationsAtom, translations],
            [componentsAtom, fgComponents],
          ]}
        >
          <CreateSmartField
            inputCustomConfig={smartFieldCreationInput}
            onValidFormStateChange={handleFormValidation}
            onFormChange={onFormChange}
            categories={categories}
            isCategoryFeatureEnabled={isCategoryFeatureEnabled}
            renderDataFileInput={(props) => (
              <ParameterModuleSmartFieldDataFileInput
                {...props}
                artifacts={artifacts}
                artifactsIsLoading={artifactsIsLoading}
                onArtifactSearch={onArtifactSearch}
                onArtifactCreate={onArtifactCreate}
                onArtifactPreview={onArtifactPreview}
              />
            )}
          />
        </HydrateAtoms>
      </Provider>
    </div>
  );
}
